import React from 'react'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import API from 'common/logic/API'
import { Button, Col, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import * as yup from 'yup'
import * as Types from 'common/utils/Types'
import { Navigate } from 'react-router-dom'
import { useState } from 'react'

interface Props {
    onSuccess: string | ((result: unknown) => void)
    entryId?: number
}

function EditUserWorkflow(props: Props) {

    const [redirect, setRedirect] = useState<string | null>(null)

    if (redirect) {
        return <Navigate to={redirect} replace />
    }

    const loadingStep = (_parameters: unknown) => {
        if (props.entryId !== undefined) {
            return API.get(`/users/${props.entryId}`).then((r: any) => ({ entry: r.data }))
        }
        return Promise.resolve({ entry: null })
    }

    const userInputStep = (_parameters: unknown, loading_result: unknown, _values: unknown, _errors: unknown, completion: (input: unknown) => void) => {
        const lr = loading_result as any
        return {
            component: form(lr.entry, completion),
            title: props.entryId ? "Edit user" : "New user"
        }
    }

    const executionStep = (_parameters: unknown, _loading_result: unknown, user_input: unknown) => {
        if (props.entryId) {
            return API.put(`/users/${props.entryId}`, user_input)
        } else {
            return API.post("/users", user_input)
        }
    }

    const completionCallback = (execution_result: unknown) => {
        if (Types.isString(props.onSuccess)) {
            setRedirect(props.onSuccess as string)
        } else {
            (props.onSuccess as (result: unknown) => void)(execution_result && (execution_result as any).data)
        }
    }

    function form(previousValues: any, completion: (input: unknown) => void) {

        const schema = yup.object().shape({
            username: yup.string().required(),
            key: yup.string().required(),
            isAdmin: yup.boolean()
        })

        const initialValues = previousValues ? {
            username: previousValues.username,
            key: '',
            isAdmin: previousValues.isAdmin
        } : {
            username: '',
            key: '',
            isAdmin: false
        }

        const parseForm = (values: any) => {
            completion({
                username: values.username,
                key: values.key,
                isAdmin: values.isAdmin
            })
        }

        return (
            <Formik validationSchema={schema} onSubmit={parseForm} initialValues={initialValues}>
                {({ handleSubmit, handleChange, values, errors }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Form.Row>
                            <Form.Group as={Col} controlId="formUsername">
                                <Form.Label>Username</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="username"
                                    placeholder="Username"
                                    value={values.username}
                                    onChange={handleChange}
                                    isInvalid={!!errors.username}
                                />
                                <Form.Control.Feedback type="invalid">{errors.username}</Form.Control.Feedback>
                            </Form.Group>
                        </Form.Row>

                        <Form.Row>
                            <Form.Group as={Col} controlId="formKey">
                                <Form.Label>Password / API key</Form.Label>
                                <Form.Control
                                    type="password"
                                    name="key"
                                    placeholder="Password / API key"
                                    value={values.key}
                                    onChange={handleChange}
                                    isInvalid={!!errors.key}
                                />
                                <Form.Control.Feedback type="invalid">{errors.key}</Form.Control.Feedback>
                            </Form.Group>
                        </Form.Row>

                        <Form.Group controlId="formIsAdmin">
                            <Form.Check
                                type="checkbox"
                                label="Administrator"
                                name="isAdmin"
                                checked={values.isAdmin}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Button variant="primary" type="submit">Save</Button>
                    </Form>
                )}
            </Formik>
        )
    }

    return (
        <OperationWorkflowModal
            loadingStep={loadingStep}
            userInputStep={userInputStep}
            executionStep={executionStep}
            completionCallback={completionCallback}
        />
    )
}

export default EditUserWorkflow
